import type { Cents } from '../banking/Currency';
import type { GuidanceSessionType } from '../guidance-session/GuidanceSession';
import {
    MEMBERSHIP_BILLING_FREQUENCIES,
    MembershipBillingFrequency,
    type StripeSubscriptionStatus,
} from '../stripe/StripeTypes';

/**
 * Identity for Stripe Products, fka MembershipType, sometimes referred to as
 * 'tiers'. String enum so call sites use `SubscriptionProductId.PREMIUM`
 * instead of anonymous `'premium'` literals. HTTP still yields `string` —
 * convert with `isSubscriptionProductId`.
 */
export enum SubscriptionProductId {
    TRIAL_PREMIUM = 'trial_premium',
    PREMIUM = 'premium',
    STANDARD = 'standard',
    PLUS = 'plus',
    ESSENTIAL = 'essential',
    JOINT = 'joint',
    SOLO = 'solo',
}

const SubscriptionProductIdValues: SubscriptionProductId[] = Object.values(
    SubscriptionProductId,
);

export const isSubscriptionProductId = (
    value: string | null | undefined,
): value is SubscriptionProductId =>
    value != null &&
    (SubscriptionProductIdValues as readonly string[]).includes(value);

/** Mirrors the price ids configured per product on the backend catalog. */
export const SUBSCRIPTION_PRODUCT_BILLING_FREQUENCIES: Record<
    SubscriptionProductId,
    readonly MembershipBillingFrequency[]
> = {
    [SubscriptionProductId.TRIAL_PREMIUM]: [
        MembershipBillingFrequency.QUARTERLY,
    ],
    [SubscriptionProductId.PREMIUM]: [
        MembershipBillingFrequency.QUARTERLY,
        MembershipBillingFrequency.YEARLY,
    ],
    [SubscriptionProductId.STANDARD]: [MembershipBillingFrequency.YEARLY],
    [SubscriptionProductId.PLUS]: MEMBERSHIP_BILLING_FREQUENCIES,
    [SubscriptionProductId.ESSENTIAL]: MEMBERSHIP_BILLING_FREQUENCIES,
    [SubscriptionProductId.JOINT]: MEMBERSHIP_BILLING_FREQUENCIES,
    [SubscriptionProductId.SOLO]: MEMBERSHIP_BILLING_FREQUENCIES,
};

export const getDefaultBillingFrequency = (
    productId: SubscriptionProductId,
): MembershipBillingFrequency => {
    const frequencies = SUBSCRIPTION_PRODUCT_BILLING_FREQUENCIES[productId];
    return frequencies[0] ?? MembershipBillingFrequency.MONTHLY;
};

export enum CashBackPolicy {
    DD_REQUIREMENT = 'DD_REQUIREMENT',
    UNRESTRICTED = 'UNRESTRICTED',
}
export const CASH_BACK_POLICY_LABELS: Record<CashBackPolicy, string> = {
    [CashBackPolicy.DD_REQUIREMENT]: 'Direct deposit required',
    [CashBackPolicy.UNRESTRICTED]: 'Unrestricted',
};

export enum PremiumCardPolicy {
    NONE = 'NONE',
    ONE_PER_MEMBER = 'ONE_PER_MEMBER',
}
export const PREMIUM_CARD_POLICY_LABELS: Record<PremiumCardPolicy, string> = {
    [PremiumCardPolicy.NONE]: 'None',
    [PremiumCardPolicy.ONE_PER_MEMBER]: 'One per member',
};

export interface GuidanceOnboardingSessionInfo {
    guidanceSessionType: GuidanceSessionType;
    displayName: string;
}

export interface GuidanceGeneralSessionInfo {
    guidanceSessionType: GuidanceSessionType;
    displayName: string;
    /** null = unlimited; 0 would mean 'included but with no annual allowance', which is invalid. */
    annualLimit: number | null;
}

export interface SubscriptionProductGuidance {
    includesAssignedGuide: boolean;
    onboardingSessions: readonly GuidanceOnboardingSessionInfo[];
    /** Empty when the product offers no sessions outside the onboarding path. */
    generalSessions: readonly GuidanceGeneralSessionInfo[];
}

export interface SubscriptionProductBanking {
    cashBackPolicy: CashBackPolicy;
    premiumCardPolicy: PremiumCardPolicy;
    rateCashBack: string;
    rateCashAPY: string;
    rateInvestAUM: string;
    thresholdCentsInvestAUM: number;
}

export interface SubscriptionProduct {
    id: SubscriptionProductId;
    displayName: string;
    hasPlusFeatures: boolean;
    isZeroDollarProduct: boolean;
    guidance: SubscriptionProductGuidance;
    banking: SubscriptionProductBanking;
}

export interface SubscriptionPhaseSummary {
    subscriptionProductId?: SubscriptionProductId;
    product?: SubscriptionProduct;
    priceId: string;
    billingFrequency?: MembershipBillingFrequency;
    billingAmountCents?: Cents | null;
    startDate: string;
    endDate?: string | null;
}

/** JSON shape of GET /api/billing/subscription and Grove membership.subscription. */
export interface StripeSubscriptionSummary {
    id: string;
    status: StripeSubscriptionStatus;
    type?: SubscriptionProductId;
    /** Duplicates currentPhase.billingFrequency on phased subscriptions. */
    billingFrequency?: MembershipBillingFrequency;
    product?: SubscriptionProduct;
    priceId?: string;
    renewalDate: string;
    amountCents: Cents;
    /** Undefined when the subscription will not renew. */
    nextInvoiceAmountDueCents?: Cents;
    cancelAtDate?: string | null;
    cancelAtPeriodEnd: boolean;
    membershipYearEndsAt: string | null;
    scheduleId?: string | null;
    currentPhase?: SubscriptionPhaseSummary;
    nextPhase?: SubscriptionPhaseSummary | null;
    /** Only used before commercial model evolution. */
    promoCode?: string | null;
    /** Only used before commercial model evolution. */
    trialEndsDate?: string | null;
    /** Only used before commercial model evolution. */
    trialPrice?: number;
    /** Only used before commercial model evolution. */
    trialVariant?: string | null;
    /** True when Stripe has a card attached, default or not. */
    hasPaymentMethod?: boolean;
    /** True only when a default PM / source is set, which automatic collection needs. */
    hasDefaultPaymentMethod?: boolean;
}

export enum SubscriptionChangeTiming {
    COMMITMENT_END = 'commitment_end',
    PERIOD_END = 'period_end',
    IMMEDIATE = 'immediate',
}

export enum SubscriptionChangeMoney {
    NONE = 'none',
    CREATE_PRORATIONS = 'create_prorations',
    REFUND_TO_SOURCE = 'refund_to_source',
}

export enum SubscriptionChangeDirection {
    UPGRADE = 'upgrade',
    DOWNGRADE = 'downgrade',
    LATERAL = 'lateral',
    FREQUENCY_ONLY = 'frequency_only',
    LEAVE_TRIAL = 'leave_trial',
}

export interface SubscriptionChangePolicy {
    direction: SubscriptionChangeDirection;
    commercialDirection: SubscriptionChangeDirection;
    defaultTiming: SubscriptionChangeTiming;
    defaultMoney: SubscriptionChangeMoney;
    timing: SubscriptionChangeTiming;
    money: SubscriptionChangeMoney;
    isDefault: boolean;
    commentRequiredHint: boolean;
}

export interface SubscriptionChangeRequest {
    subscriptionProductId: SubscriptionProductId;
    billingFrequency: MembershipBillingFrequency;
    timing?: SubscriptionChangeTiming;
    money?: SubscriptionChangeMoney;
    comment?: string;
}

export interface SubscriptionChangePreview {
    policy: SubscriptionChangePolicy;
    switchAt: string | null;
    commitmentEndsAt: string | null;
    currentPeriodEnd: string;
    from: {
        productId: SubscriptionProductId;
        billingFrequency?: MembershipBillingFrequency;
        priceId?: string;
    };
    to: {
        productId: SubscriptionProductId;
        billingFrequency: MembershipBillingFrequency;
        priceId: string;
    };
    stripePreview: {
        amountDueNowCents: number;
        estimatedRefundCents: number;
        currency: string | null;
        lineSummaries: Array<{
            description: string | null;
            amountCents: number;
        }>;
        hasPaymentMethod: boolean;
        hasDefaultPaymentMethod: boolean;
        missingPaymentMethodForMoneyMovement: boolean;
    };
    warnings: string[];
    applyPayload: SubscriptionChangeRequest;
    commentRequiredHint: boolean;
}

export interface SubscriptionChangeApplyResult {
    subscription: StripeSubscriptionSummary;
    policy: SubscriptionChangePolicy;
    switchAt: string | null;
    scheduleUpdated: boolean;
}
